/**
 * PURPOSE: Modal específico para crear/editar actividades de programación individual en Winscore.
 * RESPONSIBILITIES: Capturar título, hora y fecha del día; permitir eliminación lógica sin campos colectivos.
 * COLLABORATORS: ProgramacionIndividualArea.jsx y endpoints legacy de programación.
 * ROUTE: winscore/src/components/Modals/ModalProgramacionIndividual/ModalProgramacionIndividual.jsx
 */
import React, { useEffect, useState } from "react";

const getTimeValue = (match) => {
  if (!match?.time_match) return "";
  return String(match.time_match).slice(0, 5);
};

const getDateValue = (match, fallbackDate) => {
  if (match?.date_match) return String(match.date_match).slice(0, 10);
  return fallbackDate || "";
};

export default function ModalProgramacionIndividual({
  open,
  setOpen,
  match,
  dayLabel,
  defaultDate,
  onSave,
  onDelete,
  saving = false,
  deleting = false,
}) {
  const [form, setForm] = useState({
    title: "",
    time: "",
    date: "",
  });

  useEffect(() => {
    if (!open) return;

    setForm({
      title: match?.tag_final || "",
      time: getTimeValue(match),
      date: getDateValue(match, defaultDate),
    });
  }, [open, match, defaultDate]);

  if (!open) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-gray-100 bg-slate-950 px-6 py-4 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">{dayLabel}</p>
          <h2 className="mt-1 text-xl font-bold">{match ? "Editar actividad" : "Nueva actividad"}</h2>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 p-6">
          <div>
            <label htmlFor="individual-title" className="mb-1 block text-sm font-semibold text-gray-700">
              Título
            </label>
            <input
              id="individual-title"
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              placeholder="Ej. Kata Individual Femenino"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="individual-time" className="mb-1 block text-sm font-semibold text-gray-700">
                Hora
              </label>
              <input
                id="individual-time"
                name="time"
                type="time"
                value={form.time}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <div>
              <label htmlFor="individual-date" className="mb-1 block text-sm font-semibold text-gray-700">
                Fecha del día
              </label>
              <input
                id="individual-date"
                name="date"
                type="date"
                value={form.date}
                onChange={handleChange}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          <p className="rounded-xl bg-blue-50 px-4 py-3 text-sm font-semibold leading-6 text-blue-800">
            La fecha se aplicará a todas las actividades de este día. La sede para Central se toma desde la primera sede de competencia configurada en Información del deporte.
          </p>

          <div className="mt-2 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            {onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={saving || deleting}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? "Eliminando..." : "Eliminar actividad"}
              </button>
            ) : (
              <span />
            )}

            <div className="flex gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={saving || deleting}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || deleting}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
